// src/components/UserMenu.jsx — header username dropdown: change credentials + sign out.
import { useState, useRef, useEffect } from 'react'
import { useDeliberateBackdropDismiss } from '../hooks/useDeliberateBackdropDismiss.js'
import { createPortal } from 'react-dom'
import { t } from '../lib/i18n.js'
import { signOut, changePassword } from '../hooks/useAuth.js'
import { updateMyPassword } from '../db/index.js'
import { Icon } from './Icon.jsx'
import { ExtensionMenuItem } from './ExtensionUpdate.jsx'

// myAgent: when the logged-in user has a roster row (agents.auth_user_id matches the
// session), the password change goes to /api/scheduler/my-credentials, which updates
// CORE (the login gate) + the scheduler login + the roster row together. Without a
// roster row (gigi) the dialog falls back to password-only via supabase.auth.updateUser.
//
// ⚠️ This used to run through editAgent → /api/scheduler/update-agent, which is gated to
// mgmt roles: for an ordinary agent it 401'd, the error was swallowed, and the dialog
// showed a checkmark over a password that could not sign in (2026-08-01). Two rules came
// out of that and both live here: a failure must SHOW, and self-service is password-only
// — the login-id rename stays an admin action in AgentModal, where the rename guard is.
export function UserMenu({ name, myAgent = null }) {
  const [open,   setOpen]   = useState(false)
  const [pwOpen, setPwOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const rosterMode = Boolean(myAgent)

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground border border-transparent hover:border-border rounded-lg px-2 py-1 transition-colors">
        <Icon name="user" size={14} /> <span className="hidden sm:inline">{name}</span>
        <span className="text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-50 bg-card border border-border rounded-xl shadow-xl py-1 min-w-[170px]">
          <ExtensionMenuItem />
          <button onClick={() => { setPwOpen(true); setOpen(false) }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors">
            <Icon name="key" size={13} /> {t('change_password')}
          </button>
          <div className="border-t border-border/50 my-1" />
          <button onClick={signOut}
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors text-destructive">
            ⏏ {t('login_sign_out')}
          </button>
        </div>
      )}
      {pwOpen && <ChangeCredentialsDialog myAgent={rosterMode ? myAgent : null} onClose={() => setPwOpen(false)} />}
    </div>
  )
}

function ChangeCredentialsDialog({ myAgent, onClose }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const rosterMode = Boolean(myAgent)
  const [pw,     setPw]     = useState('')
  const [pw2,    setPw2]    = useState('')
  const [showPw, setShowPw] = useState(false)
  const [err,    setErr]    = useState('')
  const [busy,   setBusy]   = useState(false)
  const [done,   setDone]   = useState(false)

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  async function submit(e) {
    e.preventDefault()
    if (pw.length < 6) { setErr(t('pw_too_short')); return }
    if (pw !== pw2)    { setErr(t('pw_mismatch')); return }
    setBusy(true); setErr('')
    try {
      // Roster login → the self-scoped server route (core + scheduler together).
      // No roster row (admin-only accounts like gigi) → scheduler auth directly,
      // which is the identity they actually sign in with.
      if (rosterMode) await updateMyPassword(pw)
      else            await changePassword(pw)
      setDone(true)
      setTimeout(onClose, 1600)
    } catch (e) {
      // Never swallow this. A password change that failed but looked like it worked
      // is exactly how an agent ends up locked out holding a password nobody accepts.
      setErr(e.message || t('creds_change_failed'))
      setBusy(false)
    }
  }

  const inp = 'w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'
  const canSubmit = Boolean(pw && pw2)

  // Portal to <body>: the header's backdrop-blur is a containing block for fixed
  // elements, which would otherwise anchor this modal to the header, not the screen.
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      {...backdropDismiss}>
      <form onSubmit={submit} className="bg-card border border-border rounded-xl w-full max-w-xs shadow-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold">{t('change_password')}</h2>
        </div>
        {done ? (
          <div className="px-5 py-6 text-sm text-emerald-600 dark:text-emerald-400 text-center"><Icon name="check" /> {t('pw_changed')}</div>
        ) : (
          <div className="px-5 py-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">{t('pw_new')}</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={pw} autoFocus autoComplete="new-password"
                  onChange={e => { setPw(e.target.value); setErr('') }} className={`${inp} pr-10`} />
                <button type="button" tabIndex={-1} onClick={() => setShowPw(s => !s)}
                  aria-label={t(showPw ? 'login_hide_password' : 'login_show_password')}
                  title={t(showPw ? 'login_hide_password' : 'login_show_password')}
                  className={`absolute inset-y-0 right-0 px-3 flex items-center transition-colors ${showPw ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                  <Icon name="eye" size={16} />
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">{t('pw_confirm')}</label>
              <input type={showPw ? 'text' : 'password'} value={pw2} autoComplete="new-password"
                onChange={e => { setPw2(e.target.value); setErr('') }} className={inp} />
            </div>
            {err && <div className="text-xs text-destructive">{err}</div>}
          </div>
        )}
        {!done && (
          <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
            <button type="button" onClick={onClose} className="text-sm font-medium px-4 py-2 rounded-lg border border-border hover:bg-accent transition-colors">{t('cancel')}</button>
            <button type="submit" disabled={busy || !canSubmit}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50">
              {busy ? t('login_signing_in') : t('save')}
            </button>
          </div>
        )}
      </form>
    </div>,
    document.body
  )
}
